import {
	useRovoEntitlementResource,
	type JiraRovoAiProduct,
} from './useRovoEntitlementResource.tsx';

/**
 * Whether Rovo AI features are enabled for a SPECIFIC Jira product.
 *
 * Use on product-specific surfaces, passing the product the surface belongs to.
 *
 * Source of truth for per-product enablement: membership in `rovoAiFeaturesEnabledJiraProducts`
 * decides — an empty list returns `false`, which is the point of honouring per-product enablement.
 * When the list is unavailable (resource loading/errored, or the enablement-status query was not
 * requested), it returns `false`. Feature gating is the caller's responsibility — gate at the
 * client, not here.
 *
 * ⚠️ Requires the `improve_rovo_enablement_check_through_new_apis` gate. The backing
 * `jira_rovoEnablementStatus` query field is only requested when that gate is on, so with the gate
 * off this hook always returns `false`. Enable the gate for your surface, and during rollout gate
 * the surrounding behaviour on the same gate so you don't silently disable Rovo for un-ramped users.
 */
export const useIsRovoAiEnabledForJiraProduct = (product: JiraRovoAiProduct): boolean => {
	const { data } = useRovoEntitlementResource();

	return data?.rovoEnablementStatus?.rovoAiFeaturesEnabledJiraProducts?.includes(product) ?? false;
};
